<template>
    <div class="w-full flex-auto flex flex-col p-8 bg-gradient-to-br from-gray-100 to-blue-50">
        <div class="h-full w-full container mx-auto">
            <div class="h-full flex flex-col bg-white rounded-2xl shadow-lg p-8">

                <!-- Header -->
                <div class="mb-6">
                    <h1 class="text-2xl font-bold text-gray-800">🤝 Academic Industry Engagement Hub Details</h1>
                    <p class="text-sm text-gray-500">Submitted on {{ formatDate(item.created_at) }}</p>

                    <!-- Submitter Info -->
                    <div v-if="item.user" class="mt-3 bg-gray-50 border rounded-lg p-4 text-sm">
                        <p class="text-gray-600">
                            <span class="font-semibold text-gray-700 mr-1">Submitted by:</span>{{ item.user.name }}
                        </p>
                        <p class="text-gray-600">
                            <span class="font-semibold text-gray-700">Email:</span>
                            <a :href="`mailto:${item.user.email}`" class="ml-1 text-blue-600 hover:underline">{{
                                item.user.email }}</a>
                        </p>
                        <p v-if="item.user.phone_number" class="text-gray-600">
                            <span class="font-semibold text-gray-700">Phone:</span>
                            <a :href="`tel:${item.user.phone_number}`" class="ml-1 hover:underline">{{
                                item.user.phone_number
                                }}</a>
                        </p>
                        <p v-if="item.user.entity" class="text-gray-600">
                            <span class="font-semibold text-gray-700">{{ primaryLabel(item.user.entity) }}:</span>
                            <span class="ml-1">{{ primaryEntity(item.user.entity) }}</span>
                        </p>
                        <p v-if="item.user.entity && secondaryEntities(item.user.entity).length" class="text-gray-600">
                            <span class="font-semibold text-gray-700">{{ secondaryLabel(item.user.entity) }}:</span>
                            <span class="ml-1">{{ secondaryEntities(item.user.entity).join(', ') }}</span>
                        </p>
                        <p v-if="item.user.campuses" class="text-gray-600">
                            <span class="font-semibold text-gray-700">Campus:</span>
                            <span class="ml-1">{{ item.user.campuses.split(',')[0] }}</span>
                        </p>
                        <p v-if="item.user.role_display_name && item.user.role_display_name.trim()"
                            class="text-gray-600">
                            <span class="font-semibold text-gray-700">User Type:</span>
                            <span class="ml-1">{{ item.user.role_display_name }}</span>
                        </p>
                        <p v-if="item.user.userDepartments && item.user.userDepartments.length" class="text-gray-600">
                            <span class="font-semibold text-gray-700">{{ getDepartmentLabel(item.user.userDepartments)
                                }}:</span>
                            <span class="ml-1">{{ getDepartmentsText(item.user.userDepartments) }}</span>
                        </p>
                        <p v-else class="text-gray-600">
                            <span class="font-semibold text-gray-700">Department:</span>
                            <span class="ml-1">Not specified</span>
                        </p>
                    </div>
                </div>

                <!-- Title -->
                <div class="mb-6">
                    <h2 class="text-lg font-semibold text-gray-700 mb-1">📌 Title</h2>
                    <p class="text-gray-800 leading-relaxed">{{ item.title || '—' }}</p>
                </div>

                <!-- Description -->
                <div class="mb-6">
                    <h2 class="text-lg font-semibold text-gray-700 mb-1">📝 Description</h2>
                    <p class="text-gray-800 leading-relaxed whitespace-pre-line">{{ item.description || '—' }}</p>
                </div>

                <!-- Student-specific -->
                <div v-if="item.created_by_role === 'user'" class="mb-6 space-y-2">
                    <h2 class="text-lg font-semibold text-gray-700 mb-2">🎓 Student Engagement Info</h2>
                    <p><strong>Expected Outcome:</strong> {{ item.expected_outcome || '—' }}</p>
                    <p><strong>Institutional Mentor:</strong> {{ item.mentor || '—' }}</p>
                    <p><strong>Industry Partner:</strong> {{ item.industry_partner || '—' }}</p>
                </div>

                <!-- Faculty-specific -->
                <div v-else class="mb-6 space-y-2">
                    <h2 class="text-lg font-semibold text-gray-700 mb-2">🎓 Faculty Engagement Info</h2>
                    <p><strong>Engagement Type:</strong> {{ item.engagement_type || '—' }}</p>
                    <p><strong>Date of Engagement:</strong> {{ item.date_of_engagement ?
                        getFormattedDate(item.date_of_engagement) : '—' }}</p>
                    <p><strong>Target Outcome:</strong> {{ item.target_outcome || '—' }}</p>
                    <p><strong>External Organization:</strong> {{ item.industry_partner || '—' }}</p>
                    <p><strong>Funded Initiative:</strong>
                        <span v-if="item.funded">Yes</span>
                        <span v-else>No</span>
                    </p>
                    <p v-if="item.funded && item.funding_details"><strong>Funding Details:</strong> {{
                        item.funding_details }}</p>
                </div>

                <!-- KPI Information -->
                <div v-if="item.kpiPillar" class="mb-6 space-y-2">
                    <h2 class="text-lg font-semibold text-gray-700 mb-2">📊 KPI Information</h2>
                    <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p><strong>KPI Pillar:</strong> {{ item.kpiPillar.id }}. {{ item.kpiPillar.title }}</p>
                        <p><strong>Pillar Weight:</strong> {{ item.kpiPillar.weight }}%</p>
                        <p v-if="item.kpiSubSection"><strong>KPI Sub-Section:</strong> {{ item.kpiSubSection.kpi_code }}
                            {{ item.kpiSubSection.title }}</p>
                    </div>
                </div>

                <!-- Attachments -->
                <div v-if="item.attachments && item.attachments.length" class="mb-6">
                    <h2 class="text-lg font-semibold text-gray-700 mb-2">📎 Uploaded Files</h2>
                    <ul class="list-disc list-inside space-y-1">
                        <li v-for="(file, index) in item.attachments" :key="index">
                            <a :href="getUrl(file.file_path)" target="_blank" class="text-blue-600 hover:underline">
                                {{ file.file_name || 'Document' }}
                            </a>
                        </li>
                    </ul>
                </div>

                <!-- No attachments -->
                <div v-else class="text-sm text-gray-400 italic mb-6">
                    No documents uploaded.
                </div>

                <!-- Back Button -->
                <div class="mt-8 flex-auto flex flex-col justify-end items-end">
                    <div @click.stop="$emit('close')"
                        class="inline-flex items-center px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition cursor-pointer">
                        ← Back to List
                    </div>
                </div>

            </div>
        </div>
    </div>
</template>

<script>
import { formattedDate } from "../../helpers/utils";
export default {
    props: ["item"],
    methods: {
        primaryEntity(entityStr) {
            const list = (entityStr || '').split(',').map(s => s.trim()).filter(Boolean);
            return list.length ? this.mapAths(list[0]) : '';
        },
        primaryLabel(entityStr) {
            const list = (entityStr || '').split(',').map(s => s.trim()).filter(Boolean);
            return list.length <= 1 ? 'Entity' : 'Primary Entity';
        },
        secondaryEntities(entityStr) {
            const list = (entityStr || '').split(',').map(s => s.trim()).filter(Boolean);
            if (list.length <= 1) return [];
            return list.slice(1).map(this.mapAths);
        },
        secondaryLabel(entityStr) {
            const count = this.secondaryEntities(entityStr).length;
            return count <= 1 ? 'Secondary Entity' : 'Secondary Entities';
        },
        mapAths(value) {
            return value === 'ATHS' ? 'ATS' : value;
        },
        formatDate(dateStr) {
            const d = new Date(dateStr)
            return d.toLocaleDateString('en-GB', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            })
        },
        getUrl(url) {
            return process.env.VUE_APP_PUBLIC_URL + url;
        },
        getFormattedDate(date) {
            return formattedDate(date);
        },
        getDepartmentLabel(userDepartments) {
            if (!userDepartments || userDepartments.length === 0) {
                return 'Department';
            }
            return userDepartments.length > 1 ? 'Departments' : 'Department';
        },
        getDepartmentsText(userDepartments) {
            if (!userDepartments || userDepartments.length === 0) {
                return 'Not specified';
            }
            return userDepartments.map(dept => dept.department).join(', ');
        }
    }
}
</script>
